import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { BankAccountDocuments } from 'bank-documents'
import { HistoryTable } from 'bank-entity-history/src/components/HistoryTable/HistoryTable'
import { routes } from 'commons/constants/routes'
import { TableDataQa } from 'commons/types/enums'
import { Layer, Toast } from 'ui/components'
import { Button, IconButton } from 'ui/components/Buttons'
import { IconName } from 'ui/components/Icon'
import { Page, PageFace, PageSection, PageSectionFace } from 'ui/components/Page'
import { useConfirmation } from 'ui/hooks'
import { BreadCrumbs } from 'ui/temp'

import { handlerServerError } from '@orbit/errors'

import { hooks } from '../../hooks'
import { GeneralInformation } from './components'
import { useBankAccountDetails } from './useDetails'

import * as SC from './styles'

const Details = () => {
  const navigate = useNavigate()
  const { bankAccountId } = useParams({ strict: false })
  const id = +(bankAccountId || 0)

  const { data, onFilterBankAccountDetails, onFilterBankAccountHistory, isFetching } = useBankAccountDetails(id)
  const {
    company: { id: companyId, name },
    history,
    historyTotalCount,
    documents,
    maskedNumber,
    form: { historyPage, historySize }
  } = data

  const { mutate: deleteBankAccount } = hooks.useDelete({
    onError: (error) => handlerServerError(error),
    onSuccess: async () => {
      Toast.successSmall('The bank account was successfully removed.')
      navigate({
        to: routes.bank.relationships.details,
        params: { id: companyId.toString() },
        hash: 'bank-accounts'
      })
    }
  })

  const { open: openDelete, Confirmation: DeleteConfirmation } = useConfirmation({
    message: `Are you sure you want to remove this bank account?`,
    onConfirm: () => deleteBankAccount({ id }),
    confirmationButtonText: 'Delete',
    isConfirmationNegative: true
  })

  return (
    <Page
      face={PageFace.SECONDARY}
      subTitle="Bank Account"
      actions={
        <IconButton icon={IconName.DELETE} face="neutral" onClick={openDelete}>
          Delete Page
        </IconButton>
      }
      isPending={isFetching}
      breadCrumbs={
        <BreadCrumbs
          links={[
            <Link to={routes.bank.relationships.details} params={{ id: companyId.toString() }} hash="bank-accounts">
              {name}
            </Link>,
            <>Bank Account&nbsp;{maskedNumber}</>
          ]}
        />
      }
    >
      <DeleteConfirmation />
      <PageSection
        face={PageSectionFace.SECONDARY}
        title="General Information"
        anchor="general"
        actions={
          <Link to="/bank-accounts/$bankAccountId/edit" params={{ bankAccountId: id.toString() }}>
            <Button face="secondary">Edit</Button>
          </Link>
        }
      >
        <SC.WrappedHr />
        {data && <GeneralInformation data={data} />}
      </PageSection>
      {documents && (
        <BankAccountDocuments
          data={data}
          documentEditCancelReturnUrl={`/bank-accounts/${id}`}
          onFilterBankAccountDetails={onFilterBankAccountDetails}
        />
      )}
      <PageSection title="History">
        <Layer rounded shadowed stretch>
          <HistoryTable
            dataQa={TableDataQa.BANK_ACCOUNT_HISTORY}
            loading={isFetching}
            items={history}
            pagination={{
              page: historyPage,
              size: historySize,
              onChange: (page, size) => onFilterBankAccountHistory({ historyPage: page, historySize: size }),
              total: historyTotalCount
            }}
          />
        </Layer>
      </PageSection>
    </Page>
  )
}

export default Details
